import AppleHealthKit, { HealthValue } from 'react-native-health'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useRef } from 'react'
import { Connections, DataMessage, getBody, initTerra } from 'terra-react'
import { Device } from '@src/config'
import { userSelector } from '@src/selectors/app'
import { getDevId } from '@src/utils/terra'
import { withTerraLock } from '@src/utils/terraLock'

const RECENT_GLUCOSE_WINDOW = 10

interface GlucoseDataSample {
  timestamp: string
  // eslint-disable-next-line camelcase
  blood_glucose_mg_per_dL: number
  // eslint-disable-next-line camelcase
  glucose_level_flag: number
  // eslint-disable-next-line camelcase
  trend_arrow: number
}

interface TerraBodyResponse {
  // eslint-disable-next-line camelcase
  glucose_data: {
    // eslint-disable-next-line camelcase
    blood_glucose_samples: Array<GlucoseDataSample>
  }
  // eslint-disable-next-line camelcase
  measurements_data: {
    // eslint-disable-next-line camelcase
    glucose_data: {
      // eslint-disable-next-line camelcase
      blood_glucose_samples: Array<GlucoseDataSample>
    }
  }
}

interface GetBodyParams {
  connection: Connections
  startDate: Date
  endDate: Date
  latestReading?: boolean
  toWebhook?: boolean
}

export const useCheckGlucoseConnection = () => {
  const dispatch = useDispatch()

  const currentUser = useSelector(userSelector)
  const terraInitialized = useRef(false)

  const getBodyWithParams = useCallback(
    ({ connection, startDate, endDate, latestReading, toWebhook }: GetBodyParams) => {
      return getBody(connection, startDate, endDate, latestReading, toWebhook)
    },
    [],
  )

  const checkTerraHealthConnectGlucose = useCallback(async () => {
    const response = (await getBodyWithParams({
      connection: Connections.HEALTH_CONNECT,
      startDate: moment().subtract(RECENT_GLUCOSE_WINDOW, 'day').toDate(),
      endDate: moment().toDate(),
      latestReading: false,
      toWebhook: false,
    })) as DataMessage

    const bodyData = (response as any).data?.data as Array<TerraBodyResponse>

    return bodyData.some(
      (daySample: TerraBodyResponse) =>
        daySample.glucose_data.blood_glucose_samples.length > 0 ||
        daySample.measurements_data.glucose_data.blood_glucose_samples.length > 0,
    )
  }, [getBodyWithParams])

  const checkHealthKitGlucose = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      const options = {
        startDate: moment().subtract(RECENT_GLUCOSE_WINDOW, 'day').toISOString(),
      }

      AppleHealthKit.getBloodGlucoseSamples(options, (_, results: HealthValue[] | undefined) => {
        const foundGlucose = !!results && results.length > 0
        // update user's health data immediately so they see it on the glucose timeline.
        if (foundGlucose) {
          dispatch({
            type: 'app/updateHealthData',
          })
        }

        resolve(foundGlucose)
      })
    })
  }, [dispatch])

  const checkGlucoseConnection = useCallback(async () => {
    if (Device.ios) {
      return checkHealthKitGlucose()
    }

    if (!terraInitialized.current && currentUser) {
      // initTerra replaces the global native Terra instance, so it must not
      // interleave with an in-flight Health Connect connection flow
      await withTerraLock(() => initTerra(getDevId(), currentUser.id.toString()))
      terraInitialized.current = true
    }

    return checkTerraHealthConnectGlucose()
  }, [checkHealthKitGlucose, checkTerraHealthConnectGlucose, currentUser])

  return checkGlucoseConnection
}
